/**
 * Phase 5 part 1 on the web table: award popups, loot Need / Want / Pass, the level-up walkthrough, auto-roll,
 * Host reward tools, and rulings appeals.
 */
import { Suspense, lazy, useCallback, useEffect, useState } from "react";
import {
  ABILITIES, CLASS_HIT_DIE, averageHpGain, abilityMod, canLevelUp, hitDieFor, totalLevel, xpForNextLevel,
  type Ability, type Appeal, type Character, type LootItem, type LootResponse,
} from "@dm/shared";
import { api } from "./api.js";
import { useGame } from "./game.js";

// ------------------------------------------------------------------ award popups

export function AwardPopups() {
  const { awards, dismissAward } = useGame();
  useEffect(() => {
    if (!awards.length) return;
    const t = setTimeout(() => dismissAward(awards[0]!.id), 9000);
    return () => clearTimeout(t);
  }, [awards, dismissAward]);
  if (!awards.length) return null;
  const icon = { xp: "⭐", gold: "🪙", milestone: "🏁", levelUp: "⬆️", loot: "🎁" } as const;
  return (
    <div className="award-popups" aria-live="polite">
      {awards.map((a) => (
        <div key={a.id} className={`award-popup award-${a.kind}`} onClick={() => dismissAward(a.id)} role="status">
          <span className="award-icon">{icon[a.kind]}</span>
          <span>{a.text}</span>
        </div>
      ))}
    </div>
  );
}

// ------------------------------------------------------------------ loot

function useLoot(): [LootResponse | null, (r: LootResponse) => void] {
  const { campaign, lootVersion } = useGame();
  const [loot, setLoot] = useState<LootResponse | null>(null);
  useEffect(() => {
    api.loot(campaign.id).then(setLoot, () => undefined);
  }, [campaign.id, lootVersion]);
  return [loot, setLoot];
}

function secondsLeft(iso: string, now: number): number {
  return Math.max(0, Math.round((Date.parse(iso) - now) / 1000));
}

/** Open loot with Need / Want / Pass for everyone (an overlay like the roll prompts). */
export function LootPrompts() {
  const { campaign, user, isHost, myCharacter, fail } = useGame();
  const [loot, setLoot] = useLoot();
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!loot?.open.length) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [loot?.open.length]);
  if (!loot?.open.length) return null;
  const claim = (i: LootItem, choice: "need" | "want" | "pass") => api.claimLoot(campaign.id, i.id, choice).then(setLoot, fail);
  const host = (i: LootItem, op: "roll" | "cancel") => api.hostLoot(campaign.id, i.id, op).then(setLoot, fail);
  return (
    <section className="prompts loot-prompts">
      {loot.open.map((i) => {
        const mine = i.claims.find((c) => c.userId === user.discordId)?.choice;
        return (
          <div key={i.id} className="prompt loot">
            <span>
              🎁 Loot: <strong>{i.name}</strong>
              {i.qty > 1 && ` x${i.qty}`}
              {i.notes && <span className="muted"> ({i.notes})</span>}
              <span className="muted small"> · {secondsLeft(i.closesAt, now)}s</span>
              {i.claims.length > 0 && (
                <span className="muted small"> · {i.claims.map((c) => `${c.characterName ?? c.playerName}: ${c.choice}`).join(", ")}</span>
              )}
            </span>
            {myCharacter ? (
              <span className="loot-choices" role="group" aria-label={`Claim ${i.name}`}>
                {(["need", "want", "pass"] as const).map((ch) => (
                  <button key={ch} className={mine === ch ? "primary" : ""} aria-pressed={mine === ch} onClick={() => claim(i, ch)}>
                    {ch === "need" ? "Need" : ch === "want" ? "Want" : "Pass"}
                  </button>
                ))}
              </span>
            ) : (
              <span className="muted small">Create a character to claim loot</span>
            )}
            {isHost && (
              <>
                <button onClick={() => host(i, "roll")} title="Roll off now instead of waiting for the timer">Roll now</button>
                <button onClick={() => host(i, "cancel")}>Remove</button>
              </>
            )}
          </div>
        );
      })}
      <p className="muted small loot-help">Need beats Want; ties roll a d20 on the server. Pass if you don't want it.</p>
    </section>
  );
}

// ------------------------------------------------------------------ auto-roll

export function AutoRollToggle() {
  const { campaign, fail, role } = useGame();
  const [on, setOn] = useState<boolean | null>(null);
  useEffect(() => {
    if (!role) return;
    api.tableSettings(campaign.id).then((s) => setOn(s.autoRoll), () => undefined);
  }, [campaign.id, role]);
  if (on === null) return null;
  return (
    <label className="auto-roll" title='Say "Dungeon Master, I attack" and the server rolls for you when the DM asks. Roll buttons still work.'>
      <input
        type="checkbox"
        checked={on}
        onChange={(e) => {
          const v = e.target.checked;
          setOn(v);
          api.setTableSettings(campaign.id, { autoRoll: v }).then((s) => setOn(s.autoRoll), (err) => {
            setOn(!v);
            fail(err);
          });
        }}
      />
      Auto-roll for me
    </label>
  );
}

// ------------------------------------------------------------------ level up

/** "Level up" button + walkthrough: class, HP (roll or average), ASI, new features and spells, with Grok's guide. */
export function LevelUp({ character, onDone }: { character: Character; onDone: () => void }) {
  const { campaign, isHost, fail } = useGame();
  const [open, setOpen] = useState<"wizard" | "manual" | null>(null);
  const [undoing, setUndoing] = useState(false);
  const ready = canLevelUp(character, campaign.progression);
  const total = totalLevel(character.level, character.details);
  const next = total < 20 ? xpForNextLevel(total) : null;
  const canOpen = total < 20 && (ready || isHost);
  // The wizard covers the SRD classes (it loads the SRD rules on demand); other classes level up by hand.
  const srd = !character.className || !!hitDieFor(character.className) || character.details.multiclass.some((m) => !!hitDieFor(m.className));
  const undo = async () => {
    if (!window.confirm(`Undo ${character.name}'s level-up to ${character.levelUndo?.toLevel}? The sheet goes back to how it was before it.`)) return;
    setUndoing(true);
    try {
      await api.undoLevelUp(campaign.id, character.id);
      onDone();
    } catch (err) {
      fail(err);
    } finally {
      setUndoing(false);
    }
  };
  return (
    <>
      {campaign.progression === "xp" && total < 20 && (
        <span className="muted small xp-meter" title="Experience points">XP {character.details.xp.toLocaleString()} / {next?.toLocaleString()}</span>
      )}
      {canOpen && (
        <button className={ready ? "primary level-up-btn ready" : "level-up-btn"} onClick={() => setOpen(srd ? "wizard" : "manual")}
          title={ready ? "You can level up!" : "Host: grant a level"}>
          {ready ? "⬆️ Level up!" : "⬆️ Grant a level"}
        </button>
      )}
      {character.levelUndo && character.levelUndo.toLevel === total && (
        <button className="small" disabled={undoing} onClick={() => void undo()} title="Put the sheet back to how it was before the last level-up">↩ Undo level-up</button>
      )}
      {open === "wizard" && (
        <Suspense fallback={<div className="modal-backdrop"><div className="modal">Loading…</div></div>}>
          <LevelUpWizard character={character} campaignId={campaign.id} fail={fail} onClose={() => setOpen(null)} onManual={() => setOpen("manual")}
            onDone={() => { setOpen(null); onDone(); }} />
        </Suspense>
      )}
      {open === "manual" && <LevelUpDialog character={character} onClose={() => setOpen(null)} onDone={() => { setOpen(null); onDone(); }} fail={fail} campaignId={campaign.id} />}
    </>
  );
}

const LevelUpWizard = lazy(() => import("./LevelUpWizard.js"));

function LevelUpDialog({ character, onClose, onDone, fail, campaignId }: {
  character: Character; onClose: () => void; onDone: () => void; fail: (e: unknown) => void; campaignId: number;
}) {
  const classes = [character.className, ...character.details.multiclass.map((m) => m.className)].filter(Boolean);
  const [className, setClassName] = useState(classes[0] ?? "");
  const [newClass, setNewClass] = useState(false);
  const [hp, setHp] = useState<"roll" | "average">("average");
  const [hitDie, setHitDie] = useState(8);
  const [subclass, setSubclass] = useState("");
  const [asi, setAsi] = useState<Partial<Record<Ability, number>>>({});
  const [features, setFeatures] = useState("");
  const [spells, setSpells] = useState("");
  const [guide, setGuide] = useState<string | null>(null);
  const [guideBusy, setGuideBusy] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const die = hitDieFor(className) ?? hitDie;
  const con = abilityMod(character.abilities.con);
  const asiUsed = Object.values(asi).reduce((s, v) => s + (v ?? 0), 0);

  const askGuide = useCallback(async () => {
    setGuideBusy(true);
    try {
      setGuide((await api.levelGuide(campaignId, character.id, className)).text);
    } catch (err) {
      fail(err);
    } finally {
      setGuideBusy(false);
    }
  }, [campaignId, character.id, className, fail]);

  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.levelUp(campaignId, character.id, {
        className, hp, hitDie: hitDieFor(className) ? undefined : hitDie, subclass: subclass || undefined,
        abilityIncreases: asiUsed ? asi : undefined,
        features: features.split("\n").map((l) => l.trim()).filter(Boolean).map((l) => {
          const [name, ...rest] = l.split(":");
          return { name: name!.trim(), description: rest.join(":").trim() };
        }),
        spells: spells.split("\n").map((l) => l.trim()).filter(Boolean).map((l) => {
          const m = /^(.*?)\s*\((\d|cantrip)\)\s*$/i.exec(l);
          return m ? { name: m[1]!, level: m[2]!.toLowerCase() === "cantrip" ? 0 : Number(m[2]) } : { name: l, level: 1 };
        }),
      });
      setResult(`${r.summary}.${r.hpRoll !== null ? ` (Hit die roll: ${r.hpRoll})` : ""}`);
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true" aria-label="Level up">
      <div className="modal level-up">
        <h3>Level up {character.name}</h3>
        {result ? (
          <>
            <p className="note">{result}</p>
            <p className="muted small">Check your sheet: new features and spells were added; adjust anything the walkthrough couldn't know.</p>
            <div className="row-end"><button className="primary" onClick={onDone}>Done</button></div>
          </>
        ) : (
          <>
            <label>
              Class gaining the level
              {newClass ? (
                <input value={className} onChange={(e) => setClassName(e.target.value)} placeholder="e.g. Rogue" list="srd-classes" />
              ) : (
                <select value={className} onChange={(e) => (e.target.value === "__new" ? (setNewClass(true), setClassName("")) : setClassName(e.target.value))}>
                  {classes.map((c) => <option key={c} value={c}>{c}</option>)}
                  <option value="__new">Multiclass into a new class…</option>
                </select>
              )}
              <datalist id="srd-classes">{Object.keys(CLASS_HIT_DIE).map((c) => <option key={c} value={c[0]!.toUpperCase() + c.slice(1)} />)}</datalist>
            </label>
            {!hitDieFor(className) && className && (
              <label>
                Hit die (homebrew class)
                <select value={hitDie} onChange={(e) => setHitDie(Number(e.target.value))}>
                  {[6, 8, 10, 12].map((d) => <option key={d} value={d}>d{d}</option>)}
                </select>
              </label>
            )}
            <fieldset>
              <legend>Hit points</legend>
              <label className="inline"><input type="radio" checked={hp === "average"} onChange={() => setHp("average")} /> Take the average ({averageHpGain(die, con)})</label>
              <label className="inline"><input type="radio" checked={hp === "roll"} onChange={() => setHp("roll")} /> Roll d{die} {con >= 0 ? "+" : ""}{con} (on the server, logged)</label>
            </fieldset>
            <label>
              Subclass (if you choose one this level)
              <input value={subclass} onChange={(e) => setSubclass(e.target.value)} placeholder={character.details.subclass || "e.g. Champion"} />
            </label>
            <fieldset>
              <legend>Ability Score Improvement (optional, +2 total, max 20)</legend>
              <div className="asi-grid">
                {ABILITIES.map((a) => (
                  <label key={a} className="inline">
                    {a.toUpperCase()} {character.abilities[a]}
                    <input
                      type="number" min={0} max={2} value={asi[a] ?? 0}
                      onChange={(e) => setAsi({ ...asi, [a]: Math.max(0, Math.min(2, Number(e.target.value) || 0)) })}
                    />
                  </label>
                ))}
              </div>
              {asiUsed > 2 && <p className="warn small">Only 2 points in total; the first ones listed count.</p>}
            </fieldset>
            <label>
              New features (one per line, "Name: what it does")
              <textarea rows={3} value={features} onChange={(e) => setFeatures(e.target.value)} placeholder="Action Surge: one extra action, once per short rest" />
            </label>
            <label>
              New spells (one per line, "Name (level)" or "Name (cantrip)")
              <textarea rows={2} value={spells} onChange={(e) => setSpells(e.target.value)} placeholder="Misty Step (2)" />
            </label>
            <div className="guide">
              <button type="button" disabled={guideBusy || !className} onClick={askGuide}>{guideBusy ? "Asking the DM…" : "What do I get this level?"}</button>
              {guide && <pre className="guide-text">{guide}</pre>}
            </div>
            <p className="muted small">Spell slots and proficiency bonus update automatically for SRD classes.</p>
            <div className="row-end">
              <button onClick={onClose}>Cancel</button>
              <button className="primary" disabled={busy || !className} onClick={submit}>{busy ? "Levelling…" : "Level up"}</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ Host tools: rewards + loot + appeals

export function HostRewards() {
  const { campaign, party, fail } = useGame();
  const [loot] = useLoot();
  const [xp, setXp] = useState("");
  const [reason, setReason] = useState("");
  const [gold, setGold] = useState("");
  const [items, setItems] = useState("");
  const [msg, setMsg] = useState<string | null>(null);
  const run = async (fn: () => Promise<{ message: string }>) => {
    try {
      setMsg((await fn()).message);
    } catch (err) {
      fail(err);
    }
  };
  const unclaimed = loot?.recent.filter((i) => i.status === "unclaimed") ?? [];
  return (
    <section className="panel host-rewards">
      <h3>Rewards</h3>
      <div className="reward-row">
        <input placeholder="Reason (optional)" value={reason} onChange={(e) => setReason(e.target.value)} />
      </div>
      {campaign.progression === "xp" ? (
        <div className="reward-row">
          <input type="number" min={1} placeholder="XP for the party" value={xp} onChange={(e) => setXp(e.target.value)} aria-label="XP for the party" />
          <button disabled={!Number(xp)} onClick={() => run(() => api.awardXp(campaign.id, Number(xp), reason)).then(() => setXp(""))}>Award XP (split)</button>
        </div>
      ) : (
        <div className="reward-row">
          <button onClick={() => run(() => api.awardMilestone(campaign.id, reason))}>🏁 Milestone: everyone levels up</button>
        </div>
      )}
      <div className="reward-row">
        <input type="number" min={1} placeholder="Gold pieces" value={gold} onChange={(e) => setGold(e.target.value)} aria-label="Gold pieces" />
        <button disabled={!Number(gold)} onClick={() => run(() => api.awardGold(campaign.id, { gp: Number(gold) }, reason)).then(() => setGold(""))}>Split gold (players here tonight)</button>
      </div>
      <div className="reward-row">
        <input placeholder="Loot items, comma separated" value={items} onChange={(e) => setItems(e.target.value)} aria-label="Loot items" />
        <button
          disabled={!items.trim()}
          onClick={() => api.offerLoot(campaign.id, items.split(",").map((n) => ({ name: n.trim() })).filter((i) => i.name)).then(() => { setItems(""); setMsg("Loot offered: players choose Need / Want / Pass."); }, fail)}
        >
          Offer loot
        </button>
      </div>
      {unclaimed.map((i) => (
        <div key={i.id} className="reward-row">
          <span>Unclaimed: <strong>{i.name}</strong></span>
          <select defaultValue="" onChange={(e) => e.target.value && api.hostLoot(campaign.id, i.id, "give", Number(e.target.value)).catch(fail)} aria-label={`Give ${i.name} to`}>
            <option value="">Give to…</option>
            {party.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <button onClick={() => api.hostLoot(campaign.id, i.id, "cancel").catch(fail)}>Discard</button>
        </div>
      ))}
      {msg && <p className="note small">{msg}</p>}
      {loot && loot.recent.length > 0 && (
        <details>
          <summary className="small">Recent loot</summary>
          <ul className="small">
            {loot.recent.map((i) => (
              <li key={i.id}>
                {i.name}: {i.status === "awarded" ? `${i.winnerName}${i.winTier ? ` (${i.winTier})` : ""}` : i.status}
                {i.claims.some((c) => c.rolls.length) && <span className="muted"> · rolls {i.claims.filter((c) => c.rolls.length).map((c) => `${c.characterName}: ${c.rolls.join("/")}`).join(", ")}</span>}
              </li>
            ))}
          </ul>
        </details>
      )}
    </section>
  );
}

function useAppeals(): [Appeal[], () => void] {
  const { campaign, hostVersion, appealVersion } = useGame();
  const [list, setList] = useState<Appeal[]>([]);
  const load = useCallback(() => void api.appeals(campaign.id).then((r) => setList(r.appeals), () => undefined), [campaign.id]);
  useEffect(load, [load, hostVersion, appealVersion]);
  return [list, load];
}

const STATUS_LABEL = { open: "Waiting for the Host", upheld: "Upheld (the DM's ruling stands)", overturned: "Overturned", withdrawn: "Withdrawn" } as const;

/** Host tab: open appeals with Uphold / Overturn. */
export function HostAppeals() {
  const { campaign, fail } = useGame();
  const [list, reload] = useAppeals();
  const [response, setResponse] = useState<Record<number, string>>({});
  const open = list.filter((a) => a.status === "open");
  const decide = (a: Appeal, d: "uphold" | "overturn") =>
    api.decideAppeal(campaign.id, a.id, d, response[a.id] ?? "").then(reload, fail);
  return (
    <section className="panel appeals">
      <h3>Rulings appeals {open.length > 0 && <span className="tag host">{open.length} open</span>}</h3>
      {!list.length && <p className="muted small">No appeals. Players appeal a DM ruling from the Transcript, or by saying "Dungeon Master, I appeal".</p>}
      {open.map((a) => (
        <div key={a.id} className="appeal">
          <p><strong>{a.playerName}</strong>: {a.text}</p>
          {a.lineText && <p className="muted small">DM said: "{a.lineText}"</p>}
          <textarea rows={2} placeholder="Your ruling (needed to overturn; the DM follows it)" value={response[a.id] ?? ""} onChange={(e) => setResponse({ ...response, [a.id]: e.target.value })} />
          <div className="row-end">
            <button onClick={() => decide(a, "uphold")}>Uphold the DM</button>
            <button className="primary" disabled={!(response[a.id] ?? "").trim()} onClick={() => decide(a, "overturn")}>Overturn</button>
          </div>
        </div>
      ))}
      {list.filter((a) => a.status !== "open").length > 0 && (
        <details>
          <summary className="small">Decided</summary>
          <ul className="small">
            {list.filter((a) => a.status !== "open").map((a) => (
              <li key={a.id}>{a.playerName}: {a.text} → <em>{STATUS_LABEL[a.status]}</em>{a.response && `: ${a.response}`}{a.decidedByName && ` (${a.decidedByName})`}</li>
            ))}
          </ul>
        </details>
      )}
    </section>
  );
}

/** Private tab: my appeals and what the Host decided. */
export function MyAppeals() {
  const { campaign, fail } = useGame();
  const [list, reload] = useAppeals();
  if (!list.length) return null;
  return (
    <section className="panel appeals">
      <h3>My appeals</h3>
      <ul className="small">
        {list.map((a) => (
          <li key={a.id}>
            {a.text} → <em>{STATUS_LABEL[a.status]}</em>{a.response && `: ${a.response}`}
            {a.status === "open" && <button className="small-btn" onClick={() => api.withdrawAppeal(campaign.id, a.id).then(reload, fail)}>Withdraw</button>}
          </li>
        ))}
      </ul>
    </section>
  );
}

/** "Appeal" on a DM line (Transcript). */
export function AppealButton({ lineId, text }: { lineId: number; text: string }) {
  const { campaign, role, fail } = useGame();
  const [sent, setSent] = useState(false);
  if (!role) return null;
  return (
    <button
      className="link-btn small appeal-btn"
      disabled={sent}
      title="Ask the Host to review this ruling"
      onClick={async () => {
        const why = window.prompt(`Appeal this ruling to the Host?\n\n"${text.slice(0, 200)}"\n\nWhat should change, and why?`);
        if (!why?.trim()) return;
        try {
          await api.appeal(campaign.id, why.trim(), lineId);
          setSent(true);
        } catch (err) {
          fail(err);
        }
      }}
    >
      {sent ? "Appealed" : "⚖ Appeal"}
    </button>
  );
}
